/**
 * WordGraph — force-directed semantic graph on canvas.
 *
 * Pill-shaped nodes with word labels inside. Nodes breathe gently
 * when unexpanded; the target pulses with a coral ring beacon.
 *
 * Zoom strategy:
 *   - Small graphs (<=10 nodes): zoom-to-fit the whole graph.
 *   - Larger graphs: build the "visible set" (1-hop neighborhood of
 *     the expanded node + newly created nodes), then check whether
 *     all of them are already in the viewport. If they are, the
 *     camera stays put; otherwise zoom-to-fit the visible set with
 *     generous padding to reduce constant reframing.
 *
 * Physics:
 *   - Link distance is similarity-based: similar words sit closer.
 *   - Charge repulsion is distance-capped so distant clusters
 *     stop pushing each other away.
 */

import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import useTouchGuard from '../hooks/useTouchGuard';

// ---------------------------------------------------------------------------
// Responsive helpers
// ---------------------------------------------------------------------------

/** True when the primary input is touch (mobile/tablet). */
const IS_TOUCH = typeof window !== 'undefined'
  && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

/** Screen-width–aware scale factor for canvas rendering. */
function getNodeScale() {
  if (typeof window === 'undefined') return 1;
  const w = window.innerWidth;
  if (w <= 380) return 0.7;
  if (w <= 540) return 0.8;
  if (w <= 768) return 0.9;
  return 1;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Physics
const LINK_DIST_MIN    = 100;   // for similarity ≈ 1.0
const LINK_DIST_MAX    = 260;   // for similarity ≈ 0.0
const LINK_DIST_RANGE  = LINK_DIST_MAX - LINK_DIST_MIN;
const CHARGE_STRENGTH  = -2200;
const CHARGE_MAX_DIST  = 450;
const CENTER_STRENGTH  = 0.035;

// Node rendering (base values — scaled by getNodeScale() at render time)
const PAD_X_BASE     = 20;
const PAD_Y_BASE     = 12;
const FONT_SIZE_BASE = 15;
const FONT           = "'Space Grotesk', 'Inter', sans-serif";

// Zoom & simulation pinning
const SMALL_GRAPH_THRESHOLD = 10;
const ZOOM_DELAY_FULL  = 250;
const ZOOM_DELAY_LOCAL = 400;
const ZOOM_PAD_FULL    = 100;
const ZOOM_PAD_LOCAL   = 140;
const ZOOM_DURATION    = 500;
const LOCAL_DEPTH      = 4;
const SETTLE_DURATION  = 800;

// Combine drag — larger radius on touch for easier finger targeting
const COMBINE_DETECT_RADIUS  = IS_TOUCH ? 110 : 80;
// Freeze radius — when the dragged node is this close to ANY other node,
// all non-dragged nodes are pinned so the graph stops jittering and the
// player can aim precisely. Slightly larger than combine detect so the
// graph stabilises before the combine indicator appears.
const DRAG_FREEZE_RADIUS = IS_TOUCH ? 165 : 120;

// Edge-pan — when dragging a node near the viewport edge, the camera
// scrolls in that direction so the player can reach off-screen areas
// without zooming out. Wider edge zone on touch for fat-finger comfort.
const EDGE_PAN_ZONE   = IS_TOUCH ? 70 : 50;   // px from viewport edge
const EDGE_PAN_SPEED  = IS_TOUCH ? 3 : 5;    // graph-units per frame at zoom 1

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

// Accent quad: blue=#4a8fd4, green=#38d97a, gold=#daa520, red=#d94848
// Node mapping: Start=blue, Explored=green, Fresh/New=gold, Target=red
const C = {
  bg:         '#0a0e17',

  // Default unexpanded node — neutral cool
  fillTop:    '#151c2c',  fillBot:    '#101722',
  // Explored — green derivatives
  expTop:     '#0e2218',  expBot:     '#0a1a12',
  // Target — red derivatives
  targetTop:  '#2a1218',  targetBot:  '#1e0c10',
  // Winner — green (brighter)
  winnerTop:  '#122a18',  winnerBot:  '#0c1e10',
  // Start — blue derivatives
  startTop:   '#122440',  startBot:   '#0c1a30',

  border:      'rgba(140,170,220,0.12)',
  // Explored border — green
  borderExp:   'rgba(56,217,122,0.30)',
  // Selected border — green
  borderSel:   'rgba(56,217,122,0.55)',
  // Target border — red
  borderTgt:   'rgba(217,72,72,0.40)',
  // Winner border — green
  borderWin:   'rgba(56,217,122,0.70)',
  // Start border — blue
  borderStart: 'rgba(74,143,212,0.25)',

  text:       '#b0b8c8',
  textBright: '#e8edf5',
  textMuted:  '#3d4756',

  glowTarget: 'rgba(217,72,72,0.18)',
  glowWinner: 'rgba(56,217,122,0.25)',
  glowSelect: 'rgba(56,217,122,0.15)',

  // Fresh/New — gold derivatives
  freshTop:    '#1e1a0c',  freshBot:    '#161208',
  borderFresh: 'rgba(218,165,32,0.40)',
  glowFresh:   'rgba(218,165,32,0.15)',
  edgeFresh:   'rgba(218,165,32,0.40)',

  // Combine parent — gold
  borderCombineParent: 'rgba(218,165,32,0.50)',
  glowCombineParent:   'rgba(218,165,32,0.10)',

  // Search highlight — bright white
  borderSearch: 'rgba(230,240,255,0.75)',
  glowSearch:   'rgba(200,220,255,0.20)',

  // Bomb mode — red
  borderBomb: 'rgba(217,72,72,0.65)',
  glowBomb:   'rgba(217,72,72,0.30)',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Stadium / pill path centered at (x, y). */
function pillPath(ctx, x, y, w, h) {
  const r = h / 2;
  ctx.beginPath();
  ctx.moveTo(x - w / 2 + r, y - h / 2);
  ctx.lineTo(x + w / 2 - r, y - h / 2);
  ctx.arcTo(x + w / 2, y - h / 2, x + w / 2, y + h / 2, r);
  ctx.arcTo(x + w / 2, y + h / 2, x - w / 2, y + h / 2, r);
  ctx.lineTo(x - w / 2 + r, y + h / 2);
  ctx.arcTo(x - w / 2, y + h / 2, x - w / 2, y - h / 2, r);
  ctx.arcTo(x - w / 2, y - h / 2, x + w / 2, y - h / 2, r);
  ctx.closePath();
}

/** BFS from sourceId over graph links; returns Map<nodeId, depth>. */
function bfsDepth(sourceId, links) {
  const adj = new Map();
  for (const link of links) {
    const s = typeof link.source === 'object' ? link.source?.id : link.source;
    const t = typeof link.target === 'object' ? link.target?.id : link.target;
    if (!s || !t) continue;
    if (!adj.has(s)) adj.set(s, []);
    if (!adj.has(t)) adj.set(t, []);
    adj.get(s).push(t);
    adj.get(t).push(s);
  }
  const depth = new Map();
  const queue = [sourceId];
  depth.set(sourceId, 0);
  while (queue.length) {
    const id = queue.shift();
    const d = depth.get(id);
    for (const nb of adj.get(id) || []) {
      if (!depth.has(nb)) {
        depth.set(nb, d + 1);
        queue.push(nb);
      }
    }
  }
  return depth;
}


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Height reserved for the mobile bottom stats bar. */
const MOBILE_BAR_HEIGHT = 46;
const MOBILE_BREAKPOINT = 540;

function getCanvasDimensions() {
  const w = window.innerWidth;
  const h = window.innerHeight - (w <= MOBILE_BREAKPOINT ? MOBILE_BAR_HEIGHT : 0);
  return { width: w, height: h };
}

export default function WordGraph({
  nodes,
  links,
  onNodeClick,
  onCombine,
  selectedWord,
  targetWord,
  winningWord,
  victoryPending,
  initialWord,
  lastExpandedWord,
  expansionFocusIds,
  lastCombineResult,
  searchedWord,
  loading,
  disabled = false,
  bombMode = false,
  shortestPathEdges = null,
  warmth = 0,
}) {
  const graphRef = useRef();
  const [hoveredId, setHoveredId] = useState(null);
  const prevCount = useRef(0);

  // Pop animation — track newly added node IDs with their birth time
  const newNodeBirths = useRef(new Map()); // nodeId → timestamp
  const POP_DURATION = 300; // ms

  // Explicit canvas dimensions — force-graph defaults to window size which
  // doesn't account for mobile bottom bar. Only updates when width changes
  // significantly (orientation, DevTools toggle), ignoring height-only shifts
  // from the mobile address bar hiding/showing (which would reset zoom).
  const [dimensions, setDimensions] = useState(getCanvasDimensions);
  const isMobile = dimensions.width <= MOBILE_BREAKPOINT;
  const nodeScaleRef = useRef(getNodeScale());

  useEffect(() => {
    const onResize = () => {
      nodeScaleRef.current = getNodeScale();
      const next = getCanvasDimensions();
      setDimensions(prev => {
        if (Math.abs(next.width - prev.width) < 50) return prev;
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Prevent browser default touch gestures (scroll, pinch-zoom) on the graph.
  // See hooks/useTouchGuard.js for full explanation of why this is needed.
  const touchGuardRef = useTouchGuard();

  // Clean up edge-pan rAF on unmount
  useEffect(() => () => { if (panFrameRef.current) cancelAnimationFrame(panFrameRef.current); }, []);

  // Combine state — tracked via refs to avoid re-renders during drag
  const dragNodeRef = useRef(null);
  const isDraggingRef = useRef(false);
  const combineTargetRef = useRef(null);
  const [combineTarget, setCombineTarget] = useState(null); // for rendering
  // Freeze state — when the dragged node is near others, all non-dragged
  // nodes are pinned (fx/fy) so the graph layout stays perfectly still.
  const isFrozenRef = useRef(false);
  const frozenNodesRef = useRef([]);
  // Set to true once edge-pan has actually shifted nodes. When true, the
  // lock stays sticky for the rest of the drag to prevent center-force
  // snap-back. Proximity-only locks can still be released.
  const hasPannedRef = useRef(false);
  // Edge-pan — rAF loop for continuous scrolling while pointer is at a
  // screen edge. Instead of calling centerAt (which modifies the d3-zoom
  // transform and conflicts with d3-drag), we shift every non-dragged
  // node in the opposite direction — visually identical to a viewport pan
  // but leaves the zoom transform untouched so d3-drag works perfectly.
  const panVelocityRef = useRef({ dx: 0, dy: 0 });
  const panFrameRef = useRef(null);
  // Always-current reference to graphData.nodes so the rAF closure never
  // reads a stale memoized array.
  const graphNodesRef = useRef([]);

  // Deduplicated graph data ------------------------------------------------

  const graphData = useMemo(() => {
    const map = new Map();
    for (const n of nodes || []) if (n?.id) map.set(n.id, n);
    return {
      nodes: Array.from(map.values()),
      links: (links || []).map(l => ({
        source: l.source,
        target: l.target,
        similarity: l.similarity,
      })),
    };
  }, [nodes, links]);
  graphNodesRef.current = graphData.nodes;

  // Force configuration ----------------------------------------------------
  // Similarity-based link distance + distance-capped charge.
  // Re-runs on graphData change to handle ForceGraph2D remounts.

  useEffect(() => {
    const fg = graphRef.current;
    if (!fg) return;

    fg.d3Force('link')
      ?.id(d => d.id)
      .distance(link => {
        const sim = link.similarity || 0.3;
        return LINK_DIST_MAX - sim * LINK_DIST_RANGE;
      });

    fg.d3Force('charge')
      ?.strength(CHARGE_STRENGTH)
      .distanceMax(CHARGE_MAX_DIST);

    fg.d3Force('center')?.strength(CENTER_STRENGTH);
  }, [graphData]);

  // Expansion handling: pin distant nodes + smart zoom ----------------------
  // Fires when nodes are added (count increases). Two responsibilities:
  //
  // 1. Pin — BFS from the expanded node; freeze nodes beyond LOCAL_DEPTH so
  //    only the nearby neighborhood participates in the force simulation.
  //    Distant clusters stay perfectly still instead of jittering.
  //
  // 2. Zoom — after the local simulation partially settles, check whether
  //    the 1-hop neighborhood + new outputs are in the viewport. If not,
  //    zoom-to-fit them with generous padding. Skip if they're already visible.
  //
  // Pins are released after SETTLE_DURATION so future interactions aren't blocked.

  useEffect(() => {
    const count = nodes?.length || 0;
    if (count <= prevCount.current && count !== 1) {
      // Node count didn't grow (and it's not a fresh single-node state)
      prevCount.current = count;
      return;
    }

    // Record birth time for new nodes (powers pop animation)
    const now = Date.now();
    if (expansionFocusIds) {
      for (const id of expansionFocusIds) {
        if (!newNodeBirths.current.has(id)) {
          newNodeBirths.current.set(id, now);
        }
      }
    }
    // Clean old entries (>2s) to prevent memory leak
    for (const [id, birth] of newNodeBirths.current) {
      if (now - birth > 2000) newNodeBirths.current.delete(id);
    }

    const fg = graphRef.current;
    if (!fg) { prevCount.current = count; return; }
    const isSmall = count <= SMALL_GRAPH_THRESHOLD;
    prevCount.current = count;

    // --- Single node (new game or first load): center at comfortable zoom ---
    if (count === 1) {
      const timer = setTimeout(() => {
        if (!fg) return;
        fg.centerAt(0, 0, ZOOM_DURATION);
        fg.zoom(1.5, ZOOM_DURATION);
      }, 50);
      return () => clearTimeout(timer);
    }

    // --- Small graph: simple zoom-to-fit, no pinning needed ---
    if (isSmall) {
      const pad = isMobile ? 40 : ZOOM_PAD_FULL;
      const timer = setTimeout(() => {
        if (fg) fg.zoomToFit(ZOOM_DURATION, pad);
      }, ZOOM_DELAY_FULL);
      return () => clearTimeout(timer);
    }

    // Determine the BFS origin(s) for pinning + zoom targeting.
    // Expansions: the expanded word. Combines: the two parent words.
    const isCombine = !lastExpandedWord && lastCombineResult?.parents?.length;
    const bfsOrigins = lastExpandedWord
      ? [lastExpandedWord]
      : isCombine
        ? lastCombineResult.parents
        : null;

    if (!bfsOrigins) return;

    // --- Pin distant nodes so only the local neighborhood moves ---
    // Access d3 simulation nodes via the link force (the React wrapper
    // doesn't expose graphData() on the ref — it's handled as a prop).
    const linkForce = fg.d3Force('link');
    const simLinks = linkForce?.links() || [];

    // Build node map from resolved link endpoints (these ARE the d3 node
    // objects with x/y/fx/fy — mutating them controls the simulation).
    const nodeMap = new Map();
    for (const link of simLinks) {
      if (link.source?.id) nodeMap.set(link.source.id, link.source);
      if (link.target?.id) nodeMap.set(link.target.id, link.target);
    }

    // BFS from the origin(s) to find the local neighborhood.
    // For combines, the new word won't be in simLinks yet (d3 hasn't resolved
    // it), so also BFS from parent nodes via expansionFocusIds to ensure
    // the combine parents stay unpinned and can settle normally.
    const depths = bfsDepth(bfsOrigins[0], simLinks);
    for (let i = 1; i < bfsOrigins.length; i++) {
      const extra = bfsDepth(bfsOrigins[i], simLinks);
      for (const [nid, d] of extra) {
        if (!depths.has(nid) || d < depths.get(nid)) {
          depths.set(nid, d);
        }
      }
    }
    if (expansionFocusIds) {
      for (const id of expansionFocusIds) {
        if (!depths.has(id)) {
          const extra = bfsDepth(id, simLinks);
          for (const [nid, d] of extra) {
            if (!depths.has(nid) || d < depths.get(nid)) {
              depths.set(nid, d);
            }
          }
        }
      }
    }

    const pinned = [];
    for (const node of nodeMap.values()) {
      const d = depths.get(node.id);
      if ((d === undefined || d > LOCAL_DEPTH) && node.x !== undefined) {
        node.fx = node.x;
        node.fy = node.y;
        pinned.push(node);
      }
    }

    // Give the simulation a local energy bump so the new node(s) settle properly.
    // Distant nodes are pinned above, so this only moves the local neighborhood.
    // Especially needed for combine (1 new node) where default alpha may be too low.
    fg.d3ReheatSimulation?.();

    // --- Smart zoom after simulation partially settles ---
    const zoomTimer = setTimeout(() => {
      if (!fg) return;

      // The zoom target is just the origin(s) + the new output nodes
      // (expansionFocusIds). Don't include the entire 1-hop neighborhood
      // — that pulls in too many nodes and zooms out excessively.
      const zoomTarget = new Set(bfsOrigins);
      if (expansionFocusIds) {
        for (const id of expansionFocusIds) zoomTarget.add(id);
      }

      // Only zoom if any of the NEW outputs are off-screen.
      // Use expansionFocusIds (the actual new nodes) for the viewport
      // check — origins are already visible since the user just clicked.
      const topLeft = fg.screen2GraphCoords(0, 0);
      const bottomRight = fg.screen2GraphCoords(dimensions.width, dimensions.height);
      const margin = 40 / (fg.zoom() || 1);

      const newOutputs = graphData.nodes.filter(
        n => expansionFocusIds?.has(n.id) && n.x !== undefined,
      );

      const allInView = newOutputs.length > 0 && newOutputs.every(n =>
        n.x >= topLeft.x + margin && n.x <= bottomRight.x - margin &&
        n.y >= topLeft.y + margin && n.y <= bottomRight.y - margin,
      );

      if (!allInView && newOutputs.length > 0) {
        const pad = isMobile ? 30 : ZOOM_PAD_LOCAL;
        const prevZoom = fg.zoom();
        fg.zoomToFit(ZOOM_DURATION, pad, node => zoomTarget.has(node.id));
        // Don't zoom IN past the current level — only zoom out if needed.
        // zoomToFit on a small cluster of 1-2 nodes can zoom in excessively.
        requestAnimationFrame(() => {
          if (fg.zoom() > prevZoom) fg.zoom(prevZoom, ZOOM_DURATION);
        });
      }
    }, ZOOM_DELAY_LOCAL);

    // --- Unpin after simulation cools ---
    const unpinTimer = setTimeout(() => {
      for (const node of pinned) {
        node.fx = undefined;
        node.fy = undefined;
      }
    }, SETTLE_DURATION);

    return () => {
      clearTimeout(zoomTimer);
      clearTimeout(unpinTimer);
      for (const node of pinned) {
        node.fx = undefined;
        node.fy = undefined;
      }
    };
  }, [nodes, lastExpandedWord, lastCombineResult, expansionFocusIds, graphData]);

  // Search: zoom to the searched node ----------------------------------------
  useEffect(() => {
    if (!searchedWord) return;
    const fg = graphRef.current;
    if (!fg) return;
    const node = nodes.find(n => n.id === searchedWord);
    if (!node || node.x === undefined) return;
    fg.centerAt(node.x, node.y, ZOOM_DURATION);
    fg.zoom(1.8, ZOOM_DURATION);
  }, [searchedWord, nodes]);

  // Handlers ---------------------------------------------------------------

  const handleClick = useCallback(
    (node) => {
      if (disabled && !bombMode) return;
      if (onNodeClick && node?.id) onNodeClick(node.id);
    },
    [onNodeClick, disabled, bombMode],
  );

  const handleHover = useCallback(
    (node) => setHoveredId(node?.id || null),
    [],
  );

  // Drag — detect proximity to another node for combine ----------------------
  // When dragging, the dragged node's charge repulsion is disabled so it
  // doesn't push other nodes away — making it much easier to drag over a
  // node to combine. Link attraction still works normally, so connected
  // nodes will follow if the dragged node moves away from them.
  //
  // Edge-pan: instead of calling centerAt (which modifies the d3-zoom
  // transform and causes snap-back conflicts with d3-drag), we shift
  // every OTHER node in the opposite direction. Visually identical to a
  // viewport pan but the zoom transform is never touched, so d3-drag
  // keeps working perfectly. A rAF loop applies the shift continuously
  // so panning continues even when the pointer is held still at the edge.

  /** Start the edge-pan rAF loop (idempotent). */
  const startEdgePan = useCallback(() => {
    if (panFrameRef.current) return;
    const tick = () => {
      const { dx, dy } = panVelocityRef.current;
      if (dx === 0 && dy === 0) { panFrameRef.current = null; return; }
      const dragNode = dragNodeRef.current;
      if (!dragNode) { panFrameRef.current = null; return; }
      // Shift every non-dragged node in the opposite direction.
      // Nodes are already pinned (fx/fy) by the lock mechanism in
      // handleDrag, so we update both x and fx to move them smoothly.
      const allNodes = graphNodesRef.current;
      for (let i = 0; i < allNodes.length; i++) {
        const n = allNodes[i];
        if (n === dragNode || n.x === undefined) continue;
        n.x -= dx;
        n.y -= dy;
        n.fx = n.x;
        n.fy = n.y;
      }
      panFrameRef.current = requestAnimationFrame(tick);
    };
    panFrameRef.current = requestAnimationFrame(tick);
  }, []);

  /** Stop the edge-pan loop and zero out velocity. */
  const stopEdgePan = useCallback(() => {
    panVelocityRef.current = { dx: 0, dy: 0 };
    if (panFrameRef.current) {
      cancelAnimationFrame(panFrameRef.current);
      panFrameRef.current = null;
    }
  }, []);

  const handleDrag = useCallback((node) => {
    if (!node || disabled) return;
    dragNodeRef.current = node;

    // On first drag frame, disable charge repulsion for the dragged node
    // so it doesn't push other nodes away as it moves over them.
    if (!isDraggingRef.current) {
      isDraggingRef.current = true;
      const fg = graphRef.current;
      if (fg) {
        const charge = fg.d3Force('charge');
        if (charge) {
          const dragId = node.id;
          charge.strength(n => n.id === dragId ? 0 : CHARGE_STRENGTH);
          fg.d3Force('charge', charge);
        }
      }
    }

    // Edge-pan — compute velocity based on how close to each edge the
    // node's screen position is. The rAF loop reads this continuously.
    const fg = graphRef.current;
    if (fg && node.x !== undefined) {
      const screen = fg.graph2ScreenCoords(node.x, node.y);
      const zoom = fg.zoom() || 1;
      const speed = EDGE_PAN_SPEED / zoom;
      let dx = 0, dy = 0;

      if (screen.x < EDGE_PAN_ZONE) {
        dx = -speed * (1 - screen.x / EDGE_PAN_ZONE);
      } else if (screen.x > dimensions.width - EDGE_PAN_ZONE) {
        dx = speed * (1 - (dimensions.width - screen.x) / EDGE_PAN_ZONE);
      }
      if (screen.y < EDGE_PAN_ZONE) {
        dy = -speed * (1 - screen.y / EDGE_PAN_ZONE);
      } else if (screen.y > dimensions.height - EDGE_PAN_ZONE) {
        dy = speed * (1 - (dimensions.height - screen.y) / EDGE_PAN_ZONE);
      }

      panVelocityRef.current = { dx, dy };
      if (dx !== 0 || dy !== 0) {
        hasPannedRef.current = true;
        startEdgePan();
      }
    }

    // Find nearest other node and its distance
    let nearest = null;
    let nearestDist = Infinity;
    for (const n of graphData.nodes) {
      if (n.id === node.id || n.x === undefined) continue;
      const ddx = n.x - node.x;
      const ddy = n.y - node.y;
      const dist = Math.sqrt(ddx * ddx + ddy * ddy);
      if (dist < nearestDist) {
        nearest = n;
        nearestDist = dist;
      }
    }

    // Lock: pin all non-dragged nodes whenever the dragged node is inside
    // the edge-pan zone OR near another node.
    // - If edge-pan has shifted nodes (hasPannedRef), keep the lock for
    //   the rest of the drag so center-force doesn't snap them back.
    // - If the lock was only from node proximity, release it when the
    //   dragged node moves away so the graph can settle normally.
    const isPanning = panVelocityRef.current.dx !== 0 || panVelocityRef.current.dy !== 0;
    const nearNode = nearestDist < DRAG_FREEZE_RADIUS;
    const shouldLock = isPanning || nearNode || hasPannedRef.current;

    if (shouldLock && !isFrozenRef.current) {
      isFrozenRef.current = true;
      const frozen = [];
      for (const n of graphData.nodes) {
        if (n.id !== node.id && n.x !== undefined) {
          n.fx = n.x;
          n.fy = n.y;
          frozen.push(n);
        }
      }
      frozenNodesRef.current = frozen;
    } else if (!shouldLock && isFrozenRef.current) {
      isFrozenRef.current = false;
      for (const n of frozenNodesRef.current) {
        n.fx = undefined;
        n.fy = undefined;
      }
      frozenNodesRef.current = [];
    }

    // Combine detection — within the tighter combine radius
    if (nearestDist < COMBINE_DETECT_RADIUS && nearest) {
      combineTargetRef.current = nearest;
      setCombineTarget({ id: nearest.id, x: nearest.x, y: nearest.y });
    } else {
      combineTargetRef.current = null;
      setCombineTarget(null);
    }
  }, [graphData.nodes, disabled, dimensions, startEdgePan]);

  const handleDragEnd = useCallback((node) => {
    if (disabled) return;
    const target = combineTargetRef.current;

    // Stop edge-panning
    stopEdgePan();

    // Restore charge repulsion and unpin ALL non-dragged nodes
    // (covers both freeze-pinned and edge-pan-pinned nodes).
    isDraggingRef.current = false;
    hasPannedRef.current = false;
    isFrozenRef.current = false;
    frozenNodesRef.current = [];
    for (const n of graphData.nodes) {
      if (n.id !== node?.id) {
        n.fx = undefined;
        n.fy = undefined;
      }
    }

    const fg = graphRef.current;
    if (fg) {
      const charge = fg.d3Force('charge');
      if (charge) {
        charge.strength(CHARGE_STRENGTH);
        fg.d3Force('charge', charge);
      }
    }

    if (node) {
      node.fx = undefined;
      node.fy = undefined;
    }

    if (target && node && node.id !== target.id && onCombine && !loading) {
      onCombine(node.id, target.id);

      // After the combine triggers a React state update + expansion effect,
      // ensure both parent nodes are truly free. The expansion effect runs
      // asynchronously and might not see these nodes as "local" since the
      // new combined word hasn't been resolved in d3 links yet.
      const dragId = node.id;
      const targetId = target.id;
      setTimeout(() => {
        const fg = graphRef.current;
        if (!fg) return;
        const linkForce = fg.d3Force('link');
        const simLinks = linkForce?.links() || [];
        for (const link of simLinks) {
          for (const n of [link.source, link.target]) {
            if (n?.id === dragId || n?.id === targetId) {
              n.fx = undefined;
              n.fy = undefined;
            }
          }
        }
      }, 50);
    }

    dragNodeRef.current = null;
    combineTargetRef.current = null;
    setCombineTarget(null);
  }, [onCombine, loading, disabled, stopEdgePan, graphData.nodes]);

  // -----------------------------------------------------------------------
  // Node painting
  // -----------------------------------------------------------------------

  const paintNode = useCallback((node, ctx) => {
    if (!node || node.x === undefined) return;

    const titleCase = (node.id || '').replace(/\b\w/g, c => c.toUpperCase());
    const label = node.emoji ? `${titleCase} ${node.emoji}` : titleCase;
    const isTarget = node.id === targetWord;
    const isWinner = winningWord && node.id === winningWord;
    const isSelected = node.id === selectedWord;
    const isExpanded = node.isExpanded;
    const isStart = node.id === initialWord;
    const isFresh = expansionFocusIds?.has(node.id) && node.id !== lastExpandedWord;
    const isCombineParent = lastCombineResult?.parents?.includes(node.id) ?? false;
    const isSearched = node.id === searchedWord;
    const isHovered = node.id === hoveredId;
    const isCombineTarget = combineTarget?.id === node.id;
    const isDragNode = dragNodeRef.current?.id === node.id;
    const t = Date.now();

    // Responsive node sizing (cached — updated on resize)
    const sc = nodeScaleRef.current;
    const FONT_SIZE = Math.round(FONT_SIZE_BASE * sc);
    const PAD_X = Math.round(PAD_X_BASE * sc);
    const PAD_Y = Math.round(PAD_Y_BASE * sc);

    // Measure text for pill sizing
    ctx.font = `600 ${FONT_SIZE}px ${FONT}`;
    const textW = ctx.measureText(label).width;
    const pillW = textW + PAD_X * 2;
    const pillH = FONT_SIZE + PAD_Y * 2;

    // Pop animation for newly added nodes
    let popScale = 1.0;
    const birthTime = newNodeBirths.current.get(node.id);
    if (birthTime) {
      const age = t - birthTime;
      if (age < POP_DURATION) {
        const progress = age / POP_DURATION;
        // Overshoot ease: scale up to 1.18 then settle to 1.0
        popScale = 1 + 0.18 * Math.sin(progress * Math.PI) * (1 - progress * 0.3);
      }
    }

    // Scale up slightly on hover, combine with pop
    const scale = (isHovered ? 1.08 : 1.0) * popScale;
    ctx.save();
    ctx.translate(node.x, node.y);
    ctx.scale(scale, scale);

    // --- Start node pulsing ring (affordance — "click me") ---
    if (isStart && !isExpanded && !isWinner) {
      const pulse = 0.5 + Math.sin(t / 800) * 0.5;
      const ringR = pillH / 2 + 6 + Math.sin(t / 1000) * 2;
      pillPath(ctx, 0, 0, pillW + ringR * 2 - pillH, ringR * 2);
      ctx.strokeStyle = `rgba(74,143,212,${0.15 + pulse * 0.25})`;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // --- Target pulsing ring ---
    if (isTarget && !isWinner) {
      const pulse = 0.5 + Math.sin(t / 600) * 0.5;
      const ringR = pillH / 2 + 6 + Math.sin(t / 900) * 2;
      pillPath(ctx, 0, 0, pillW + ringR * 2 - pillH, ringR * 2);
      ctx.strokeStyle = `rgba(217,72,72,${0.12 + pulse * 0.2})`;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // --- Combine magnetize ring ---
    if (isCombineTarget || (isDragNode && combineTarget)) {
      const pulse = 0.5 + Math.sin(t / 250) * 0.5;
      const ringR = pillH / 2 + 8 + pulse * 3;
      pillPath(ctx, 0, 0, pillW + ringR * 2 - pillH, ringR * 2);
      ctx.strokeStyle = `rgba(218,165,32,${0.3 + pulse * 0.4})`;
      ctx.lineWidth = 2.5;
      ctx.stroke();
    }

    // --- Glow (ordered by priority: combine > winner > target > selected > fresh) ---
    if (isFresh && !isWinner && !isTarget && !isSelected) {
      ctx.shadowColor = C.glowFresh;
      ctx.shadowBlur = 18;
    }
    if (isSelected && !isWinner) {
      ctx.shadowColor = C.glowSelect;
      ctx.shadowBlur = 20;
    }
    if (isTarget && !isWinner) {
      ctx.shadowColor = C.glowTarget;
      ctx.shadowBlur = 16;
    }
    if (isWinner) {
      ctx.shadowColor = C.glowWinner;
      ctx.shadowBlur = 28;
    }
    if (isCombineTarget || (isDragNode && combineTarget)) {
      ctx.shadowColor = 'rgba(218,165,32,0.4)';
      ctx.shadowBlur = 30;
    }

    // --- Pill fill ---
    const grad = ctx.createLinearGradient(0, -pillH / 2, 0, pillH / 2);
    if (isWinner) {
      grad.addColorStop(0, C.winnerTop);
      grad.addColorStop(1, C.winnerBot);
    } else if (isTarget) {
      grad.addColorStop(0, C.targetTop);
      grad.addColorStop(1, C.targetBot);
    } else if (isStart) {
      grad.addColorStop(0, C.startTop);
      grad.addColorStop(1, C.startBot);
    } else if (isExpanded) {
      grad.addColorStop(0, C.expTop);
      grad.addColorStop(1, C.expBot);
    } else if (isFresh) {
      grad.addColorStop(0, C.freshTop);
      grad.addColorStop(1, C.freshBot);
    } else {
      grad.addColorStop(0, C.fillTop);
      grad.addColorStop(1, C.fillBot);
    }

    pillPath(ctx, 0, 0, pillW, pillH);
    ctx.fillStyle = grad;
    ctx.fill();

    // Reset shadow before border
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    // --- Border ---
    let borderColor = C.border;
    let borderWidth = 1;
    if (isWinner)       { borderColor = C.borderWin; borderWidth = 2; }
    else if (isSelected){ borderColor = C.borderSel; borderWidth = 2; }
    else if (isTarget)  { borderColor = C.borderTgt; borderWidth = 1.5; }
    else if (isStart)   { borderColor = C.borderStart; }
    else if (isFresh)   { borderColor = C.borderFresh; borderWidth = 1.5; }
    else if (isExpanded){ borderColor = C.borderExp; }

    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    ctx.stroke();

    // --- Contextual breathing animation for unexpanded nodes ---
    // Faster/brighter when warmth is high (closer to target), stops for expanded nodes
    if (!isExpanded && !isTarget && !isWinner && !isStart && !isCombineParent) {
      // Speed scales with warmth: 1200ms base → 600ms when warm
      const breathPeriod = 1200 - warmth * 600;
      const breathAmp = 0.03 + warmth * 0.03;
      const breath = breathAmp + Math.sin(t / breathPeriod + node.x * 0.05) * breathAmp;
      pillPath(ctx, 0, 0, pillW, pillH);
      ctx.strokeStyle = `rgba(140,170,220,${breath})`;
      ctx.lineWidth = 4;
      ctx.stroke();
    }

    // --- Combine parent outline ---
    if (isCombineParent && !isWinner && !isTarget) {
      pillPath(ctx, 0, 0, pillW + 4, pillH + 4);
      ctx.strokeStyle = C.borderCombineParent;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // --- Search highlight ---
    if (isSearched) {
      ctx.shadowColor = C.glowSearch;
      ctx.shadowBlur = 20;
      pillPath(ctx, 0, 0, pillW + 6, pillH + 6);
      ctx.strokeStyle = C.borderSearch;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }

    // --- Bomb mode hover indicator ---
    if (bombMode && isHovered && !isStart && !isTarget) {
      ctx.shadowColor = C.glowBomb;
      ctx.shadowBlur = 24;
      pillPath(ctx, 0, 0, pillW + 8, pillH + 8);
      ctx.strokeStyle = C.borderBomb;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }

    // --- Winner burst ---
    if (isWinner && victoryPending) {
      const burstR = pillH / 2 + 8;
      ctx.strokeStyle = 'rgba(56,217,122,0.5)';
      ctx.lineWidth = 2;
      for (let i = 0; i < 10; i++) {
        const a = (Math.PI * 2 * i) / 10 + t / 400;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * burstR, Math.sin(a) * burstR);
        ctx.lineTo(Math.cos(a) * (burstR + 14), Math.sin(a) * (burstR + 14));
        ctx.stroke();
      }
    }

    // --- Label ---
    ctx.font = `600 ${FONT_SIZE}px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = isWinner ? C.textBright : isTarget ? '#e07070' : isSelected ? C.textBright : isFresh ? '#d4b050' : C.text;
    ctx.fillText(label, 0, 0.5);

    // --- Colorblind-safe shape indicators (small icon top-right of pill) ---
    const iconX = pillW / 2 - 4 * sc;
    const iconY = -pillH / 2 + 4 * sc;
    const iconS = 3.5 * sc;
    ctx.lineWidth = 1.2;
    if (isTarget && !isWinner) {
      // Target: small diamond
      ctx.beginPath();
      ctx.moveTo(iconX, iconY - iconS);
      ctx.lineTo(iconX + iconS, iconY);
      ctx.lineTo(iconX, iconY + iconS);
      ctx.lineTo(iconX - iconS, iconY);
      ctx.closePath();
      ctx.fillStyle = 'rgba(217,72,72,0.7)';
      ctx.fill();
    } else if (isStart) {
      // Start: small circle
      ctx.beginPath();
      ctx.arc(iconX, iconY, iconS, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(74,143,212,0.7)';
      ctx.fill();
    } else if (isExpanded) {
      // Explored: small checkmark
      ctx.beginPath();
      ctx.moveTo(iconX - iconS * 0.7, iconY);
      ctx.lineTo(iconX - iconS * 0.1, iconY + iconS * 0.6);
      ctx.lineTo(iconX + iconS * 0.8, iconY - iconS * 0.5);
      ctx.strokeStyle = 'rgba(56,217,122,0.6)';
      ctx.stroke();
    } else if (isFresh) {
      // Fresh: small star/sparkle
      ctx.beginPath();
      for (let i = 0; i < 4; i++) {
        const a = (Math.PI / 2) * i;
        ctx.moveTo(iconX + Math.cos(a) * iconS * 0.3, iconY + Math.sin(a) * iconS * 0.3);
        ctx.lineTo(iconX + Math.cos(a) * iconS, iconY + Math.sin(a) * iconS);
      }
      ctx.strokeStyle = 'rgba(218,165,32,0.7)';
      ctx.stroke();
    }

    ctx.restore();
  }, [selectedWord, targetWord, winningWord, victoryPending, initialWord, hoveredId, expansionFocusIds, lastExpandedWord, lastCombineResult, searchedWord, combineTarget, bombMode, warmth]);

  // -----------------------------------------------------------------------
  // Link painting
  // -----------------------------------------------------------------------

  const paintLink = useCallback((link, ctx) => {
    const { source: s, target: t } = link;
    if (!s || !t || s.x === undefined) return;

    const sId = s.id || s;
    const tId = t.id || t;
    const sim = link.similarity || 0.3;

    // Check if this edge is part of the shortest path
    const edgeKey = sId < tId ? `${sId}||${tId}` : `${tId}||${sId}`;
    const isShortestPath = shortestPathEdges && shortestPathEdges.has(edgeKey);

    if (isShortestPath) {
      // Glowing path highlight — cyan/teal to contrast with gold and red
      ctx.save();
      ctx.shadowColor = 'rgba(56,217,180,0.50)';
      ctx.shadowBlur = 12;
      ctx.strokeStyle = 'rgba(56,217,180,0.70)';
      ctx.lineWidth = 3.5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(t.x, t.y);
      ctx.stroke();
      ctx.restore();
      return;
    }

    // Highlight edges from the expanded/combine-parent node to freshly created nodes
    const combineParents = lastCombineResult?.parents;
    const isFreshEdge = expansionFocusIds && (
      // Expansion: hub (lastExpandedWord) to fresh nodes
      (lastExpandedWord
        && ((sId === lastExpandedWord && expansionFocusIds.has(tId))
         || (tId === lastExpandedWord && expansionFocusIds.has(sId))))
      // Combine: parent nodes to all result nodes
      || (combineParents
        && ((combineParents.includes(sId) && expansionFocusIds.has(tId))
         || (combineParents.includes(tId) && expansionFocusIds.has(sId))))
      // Cross-links between combine results
      || (combineParents
        && expansionFocusIds.has(sId) && expansionFocusIds.has(tId))
    );

    // Dim non-path edges when shortest path is shown
    const dimFactor = shortestPathEdges ? 0.4 : 1;

    if (isFreshEdge) {
      ctx.strokeStyle = shortestPathEdges ? 'rgba(218,165,32,0.16)' : C.edgeFresh;
      ctx.lineWidth = 2;
    } else {
      const alpha = (0.06 + sim * 0.18) * dimFactor;
      ctx.strokeStyle = `rgba(140,170,220,${alpha})`;
      ctx.lineWidth = 0.6 + sim * 1.4;
    }

    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(s.x, s.y);
    ctx.lineTo(t.x, t.y);
    ctx.stroke();
  }, [lastExpandedWord, expansionFocusIds, lastCombineResult, shortestPathEdges]);

  // -----------------------------------------------------------------------
  // Hit area (matches pill shape)
  // -----------------------------------------------------------------------

  const pointerArea = useCallback((node, color, ctx) => {
    if (!node || node.x === undefined) return;
    const sc = nodeScaleRef.current;
    const fontSize = Math.round(FONT_SIZE_BASE * sc);
    const padX = Math.round(PAD_X_BASE * sc);
    const padY = Math.round(PAD_Y_BASE * sc);
    // Larger hit area on touch devices for easier tapping
    const touchPad = IS_TOUCH ? 12 : 8;
    ctx.font = `600 ${fontSize}px ${FONT}`;
    const tc = (node.id || '').replace(/\b\w/g, c => c.toUpperCase());
    const tw = ctx.measureText(node.emoji ? `${tc} ${node.emoji}` : tc).width;
    const pw = tw + padX * 2 + touchPad;
    const ph = fontSize + padY * 2 + touchPad;
    ctx.fillStyle = color;
    pillPath(ctx, node.x, node.y, pw, ph);
    ctx.fill();
  }, []);

  // Post-render: combine line + label between dragged node and target ------

  const paintCombineOverlay = useCallback((ctx) => {
    const drag = dragNodeRef.current;
    const target = combineTargetRef.current;
    if (!drag || !target || drag.x === undefined || target.x === undefined) return;

    const t = Date.now();
    const pulse = 0.5 + Math.sin(t / 250) * 0.5;
    const mx = (drag.x + target.x) / 2;
    const my = (drag.y + target.y) / 2;

    // Dashed line between the two nodes
    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = `rgba(218,165,32,${0.4 + pulse * 0.3})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(drag.x, drag.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
    ctx.setLineDash([]);

    // "COMBINE" label at midpoint
    ctx.font = `700 11px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const labelText = 'COMBINE';
    const lw = ctx.measureText(labelText).width;
    const lpad = 8;
    const lh = 18;

    // Background pill
    ctx.fillStyle = `rgba(30,24,8,${0.85 + pulse * 0.12})`;
    pillPath(ctx, mx, my, lw + lpad * 2, lh);
    ctx.fill();

    // Border
    ctx.strokeStyle = `rgba(218,165,32,${0.5 + pulse * 0.3})`;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Text
    ctx.fillStyle = `rgba(240,208,96,${0.8 + pulse * 0.2})`;
    ctx.fillText(labelText, mx, my + 0.5);

    ctx.restore();
  }, [combineTarget]);

  // Loading fallback -------------------------------------------------------

  if (!nodes?.length) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', color: '#3d4756', fontFamily: FONT, fontSize: '13px',
      }}>
        Loading...
      </div>
    );
  }

  // -----------------------------------------------------------------------

  return (
    <div ref={touchGuardRef} style={{ width: '100%', height: '100%', touchAction: 'none' }}>
      <ForceGraph2D
        ref={graphRef}
        width={dimensions.width}
        height={dimensions.height}
        graphData={graphData}
        nodeId="id"
        nodeLabel=""
        nodeCanvasObject={paintNode}
        nodePointerAreaPaint={pointerArea}
        onNodeClick={handleClick}
        onNodeHover={handleHover}
        onNodeDrag={handleDrag}
        onNodeDragEnd={handleDragEnd}
        linkCanvasObject={paintLink}
        onRenderFramePost={paintCombineOverlay}
        linkDirectionalParticles={0}
        cooldownTicks={40}
        d3VelocityDecay={0.45}
        d3AlphaDecay={0.018}
        warmupTicks={0}
        backgroundColor={C.bg}
        minZoom={0.1}
        maxZoom={10}
        enableNodeDrag
        enableZoomInteraction
        enablePanInteraction
      />
    </div>
  );
}
